// AIAG-VDA Process Flow Templates
// Pre-built templates for common manufacturing processes

import {
    ProcessStepType,
    SpecialCharacteristic,
    ProcessElement4M,
    ConnectionType,
    ProcessFlowTemplate
} from '../../types/processFlow';

// ============================================================
// AIAG-VDA 7-STEP METHODOLOGY TEMPLATE
// ============================================================
export const AIAG_VDA_7_STEP_TEMPLATE: ProcessFlowTemplate = {
    id: 'aiag-vda-7step',
    name: 'AIAG-VDA 7-Step PFMEA Process',
    description: 'Standard 7-step PFMEA workflow according to AIAG-VDA handbook',
    industry: 'All Industries',
    nodes: [
        {
            id: 'step1',
            type: 'processFlowNode',
            position: { x: 0, y: 0 },
            data: {
                label: 'Planning & Preparation',
                stepNumber: 'STEP-1',
                type: ProcessStepType.START_END,
                description: 'Define scope, team, timing, and baseline FMEA',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'step2',
            type: 'processFlowNode',
            position: { x: 0, y: 150 },
            data: {
                label: 'Structure Analysis',
                stepNumber: 'STEP-2',
                type: ProcessStepType.OPERATION,
                description: 'Define system structure, process elements, and interfaces',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'step3',
            type: 'processFlowNode',
            position: { x: 0, y: 300 },
            data: {
                label: 'Function Analysis',
                stepNumber: 'STEP-3',
                type: ProcessStepType.OPERATION,
                description: 'Define functions and requirements for each element',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'step4',
            type: 'processFlowNode',
            position: { x: 0, y: 450 },
            data: {
                label: 'Failure Analysis',
                stepNumber: 'STEP-4',
                type: ProcessStepType.INSPECTION,
                description: 'Identify failure modes, effects, and causes',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'step5',
            type: 'processFlowNode',
            position: { x: 0, y: 600 },
            data: {
                label: 'Risk Analysis',
                stepNumber: 'STEP-5',
                type: ProcessStepType.DECISION,
                description: 'Rate Severity, Occurrence, Detection; Calculate AP',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'step6',
            type: 'processFlowNode',
            position: { x: 0, y: 750 },
            data: {
                label: 'Optimization',
                stepNumber: 'STEP-6',
                type: ProcessStepType.OPERATION,
                description: 'Define and implement recommended actions',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'step7',
            type: 'processFlowNode',
            position: { x: 0, y: 900 },
            data: {
                label: 'Results Documentation',
                stepNumber: 'STEP-7',
                type: ProcessStepType.START_END,
                description: 'Document results, update Control Plan, Work Instructions',
                element4M: ProcessElement4M.METHOD,
            }
        },
    ],
    edges: [
        { id: 'e1-2', source: 'step1', target: 'step2', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e2-3', source: 'step2', target: 'step3', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e3-4', source: 'step3', target: 'step4', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e4-5', source: 'step4', target: 'step5', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e5-6', source: 'step5', target: 'step6', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e6-7', source: 'step6', target: 'step7', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
    ]
};

// ============================================================
// ASSEMBLY PROCESS TEMPLATE
// ============================================================
export const ASSEMBLY_PROCESS_TEMPLATE: ProcessFlowTemplate = {
    id: 'assembly-process',
    name: 'Assembly Process Flow',
    description: 'Standard assembly process with receiving, assembly, and final inspection',
    industry: 'Manufacturing',
    nodes: [
        {
            id: 'receiving',
            type: 'processFlowNode',
            position: { x: 0, y: 0 },
            data: {
                label: 'Receiving Inspection',
                stepNumber: 'OP-010',
                type: ProcessStepType.INSPECTION,
                description: 'Verify incoming materials and components',
                element4M: ProcessElement4M.MATERIAL,
                equipment: 'Incoming Inspection Area',
                controlMethod: 'AQL 1.0',
                sampleSize: 'c=0',
            }
        },
        {
            id: 'storage',
            type: 'processFlowNode',
            position: { x: 0, y: 200 },
            data: {
                label: 'Material Storage',
                stepNumber: 'OP-020',
                type: ProcessStepType.STORAGE,
                description: 'Store approved materials in designated areas',
                element4M: ProcessElement4M.MATERIAL,
                equipment: 'Warehouse',
            }
        },
        {
            id: 'kitting',
            type: 'processFlowNode',
            position: { x: 0, y: 400 },
            data: {
                label: 'Kitting',
                stepNumber: 'OP-030',
                type: ProcessStepType.OPERATION,
                description: 'Prepare component kits for assembly',
                element4M: ProcessElement4M.MAN,
                equipment: 'Kitting Station',
                cycleTime: 120,
            }
        },
        {
            id: 'assembly1',
            type: 'processFlowNode',
            position: { x: 0, y: 600 },
            data: {
                label: 'Sub-Assembly',
                stepNumber: 'OP-040',
                type: ProcessStepType.OPERATION,
                description: 'Assemble sub-components',
                element4M: ProcessElement4M.MACHINE,
                equipment: 'Assembly Cell 1',
                cycleTime: 180,
                specialCharacteristic: SpecialCharacteristic.SC,
            }
        },
        {
            id: 'inprocess',
            type: 'processFlowNode',
            position: { x: 0, y: 800 },
            data: {
                label: 'In-Process Check',
                stepNumber: 'INS-050',
                type: ProcessStepType.INSPECTION,
                description: 'Verify sub-assembly quality',
                element4M: ProcessElement4M.METHOD,
                controlMethod: 'Visual + Gage',
                sampleSize: '100%',
            }
        },
        {
            id: 'decision1',
            type: 'processFlowNode',
            position: { x: 0, y: 1050 },
            data: {
                label: 'Pass?',
                stepNumber: 'DEC-055',
                type: ProcessStepType.DECISION,
                description: 'Conformance decision',
                element4M: ProcessElement4M.METHOD,
            }
        },
        {
            id: 'assembly2',
            type: 'processFlowNode',
            position: { x: 0, y: 1300 },
            data: {
                label: 'Final Assembly',
                stepNumber: 'OP-060',
                type: ProcessStepType.OPERATION,
                description: 'Complete product assembly',
                element4M: ProcessElement4M.MACHINE,
                equipment: 'Assembly Cell 2',
                cycleTime: 240,
                specialCharacteristic: SpecialCharacteristic.CC,
            }
        },
        {
            id: 'testing',
            type: 'processFlowNode',
            position: { x: 0, y: 1500 },
            data: {
                label: 'Functional Test',
                stepNumber: 'INS-070',
                type: ProcessStepType.SPC_CONTROL,
                description: 'Perform functional testing',
                element4M: ProcessElement4M.MACHINE,
                equipment: 'Test Stand',
                controlMethod: 'SPC Xbar-R',
                sampleSize: 'n=5/hr',
                specialCharacteristic: SpecialCharacteristic.CC,
            }
        },
        {
            id: 'packaging',
            type: 'processFlowNode',
            position: { x: 0, y: 1700 },
            data: {
                label: 'Packaging',
                stepNumber: 'OP-080',
                type: ProcessStepType.OPERATION,
                description: 'Package finished product',
                element4M: ProcessElement4M.METHOD,
                equipment: 'Packing Station',
                cycleTime: 60,
            }
        },
        {
            id: 'shipping',
            type: 'processFlowNode',
            position: { x: 0, y: 1900 },
            data: {
                label: 'Shipping',
                stepNumber: 'OP-090',
                type: ProcessStepType.START_END,
                description: 'Ship to customer',
                element4M: ProcessElement4M.METHOD,
                equipment: 'Shipping Dock',
            }
        },
        {
            id: 'rework',
            type: 'processFlowNode',
            position: { x: 400, y: 1050 },
            data: {
                label: 'Rework Station',
                stepNumber: 'OP-055R',
                type: ProcessStepType.OPERATION,
                description: 'Rework non-conforming units',
                element4M: ProcessElement4M.MAN,
                equipment: 'Rework Area',
            }
        },
        {
            id: 'scrap',
            type: 'processFlowNode',
            position: { x: -400, y: 1050 },
            data: {
                label: 'Scrap',
                stepNumber: 'OP-055S',
                type: ProcessStepType.STORAGE,
                description: 'Dispose of non-conforming units',
                element4M: ProcessElement4M.MATERIAL,
            }
        },
    ],
    edges: [
        { id: 'e-rcv-sto', source: 'receiving', target: 'storage', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-sto-kit', source: 'storage', target: 'kitting', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-kit-as1', source: 'kitting', target: 'assembly1', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-as1-inp', source: 'assembly1', target: 'inprocess', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-inp-dec', source: 'inprocess', target: 'decision1', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-dec-as2', source: 'decision1', target: 'assembly2', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW, label: 'Pass' } },
        { id: 'e-dec-rwk', source: 'decision1', sourceHandle: 'right', target: 'rework', type: 'processFlowEdge', data: { connectionType: ConnectionType.REWORK, label: 'Fail' } },
        { id: 'e-dec-scr', source: 'decision1', sourceHandle: 'left', target: 'scrap', type: 'processFlowEdge', data: { connectionType: ConnectionType.REJECT, label: 'Scrap' } },
        { id: 'e-rwk-inp', source: 'rework', target: 'inprocess', type: 'processFlowEdge', data: { connectionType: ConnectionType.REWORK } },
        { id: 'e-as2-tst', source: 'assembly2', target: 'testing', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-tst-pkg', source: 'testing', target: 'packaging', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e-pkg-shp', source: 'packaging', target: 'shipping', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
    ]
};

// ============================================================
// MACHINING PROCESS TEMPLATE
// ============================================================
export const MACHINING_PROCESS_TEMPLATE: ProcessFlowTemplate = {
    id: 'machining-process',
    name: 'Machining Process Flow',
    description: 'CNC machining process with multiple operations',
    industry: 'Metalworking',
    nodes: [
        {
            id: 'raw',
            type: 'processFlowNode',
            position: { x: 0, y: 0 },
            data: {
                label: 'Raw Material Receiving',
                stepNumber: 'OP-010',
                type: ProcessStepType.INSPECTION,
                element4M: ProcessElement4M.MATERIAL,
                controlMethod: 'Mill Cert Review',
            }
        },
        {
            id: 'sawing',
            type: 'processFlowNode',
            position: { x: 0, y: 150 },
            data: {
                label: 'Bar Sawing',
                stepNumber: 'OP-020',
                type: ProcessStepType.OPERATION,
                element4M: ProcessElement4M.MACHINE,
                equipment: 'Band Saw',
                cycleTime: 30,
            }
        },
        {
            id: 'lathe1',
            type: 'processFlowNode',
            position: { x: 0, y: 300 },
            data: {
                label: 'CNC Lathe - Op 1',
                stepNumber: 'OP-030',
                type: ProcessStepType.OPERATION,
                element4M: ProcessElement4M.MACHINE,
                equipment: 'CNC Lathe #1',
                cycleTime: 120,
                specialCharacteristic: SpecialCharacteristic.SC,
                controlMethod: 'SPC',
            }
        },
        {
            id: 'lathe2',
            type: 'processFlowNode',
            position: { x: 0, y: 450 },
            data: {
                label: 'CNC Lathe - Op 2',
                stepNumber: 'OP-040',
                type: ProcessStepType.OPERATION,
                element4M: ProcessElement4M.MACHINE,
                equipment: 'CNC Lathe #1',
                cycleTime: 90,
                specialCharacteristic: SpecialCharacteristic.CC,
                controlMethod: 'SPC Xbar-R',
            }
        },
        {
            id: 'mill',
            type: 'processFlowNode',
            position: { x: 0, y: 600 },
            data: {
                label: 'CNC Mill',
                stepNumber: 'OP-050',
                type: ProcessStepType.OPERATION,
                element4M: ProcessElement4M.MACHINE,
                equipment: 'CNC Mill #2',
                cycleTime: 180,
            }
        },
        {
            id: 'deburr',
            type: 'processFlowNode',
            position: { x: 0, y: 750 },
            data: {
                label: 'Deburring',
                stepNumber: 'OP-060',
                type: ProcessStepType.OPERATION,
                element4M: ProcessElement4M.MAN,
                equipment: 'Deburr Station',
                cycleTime: 45,
            }
        },
        {
            id: 'cmm',
            type: 'processFlowNode',
            position: { x: 0, y: 900 },
            data: {
                label: 'CMM Inspection',
                stepNumber: 'INS-070',
                type: ProcessStepType.INSPECTION,
                element4M: ProcessElement4M.MACHINE,
                equipment: 'CMM',
                controlMethod: 'First Article + n=5/lot',
                specialCharacteristic: SpecialCharacteristic.CC,
            }
        },
    ],
    edges: [
        { id: 'e1', source: 'raw', target: 'sawing', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e2', source: 'sawing', target: 'lathe1', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e3', source: 'lathe1', target: 'lathe2', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e4', source: 'lathe2', target: 'mill', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e5', source: 'mill', target: 'deburr', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
        { id: 'e6', source: 'deburr', target: 'cmm', type: 'processFlowEdge', data: { connectionType: ConnectionType.MATERIAL_FLOW } },
    ]
};

// ============================================================
// EXPORT ALL TEMPLATES
// ============================================================
export const PROCESS_FLOW_TEMPLATES: Record<string, ProcessFlowTemplate> = {
    'aiag-vda-7step': AIAG_VDA_7_STEP_TEMPLATE,
    'assembly': ASSEMBLY_PROCESS_TEMPLATE,
    'machining': MACHINING_PROCESS_TEMPLATE,
};

export default PROCESS_FLOW_TEMPLATES;
